import React, { useMemo } from "react";
import { useHolderRealizedPriceChart } from "@/hooks/coinglass/useHolderRealizedPriceChart";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { EChartsOption } from "echarts";

interface HolderRealizedPriceChartProps {
  startDate?: string;
  endDate?: string;
  limit?: number;
  height?: string | number;
}

export const HolderRealizedPriceChart: React.FC<HolderRealizedPriceChartProps> = ({
  startDate,
  endDate,
  limit,
  height = 500,
}) => {
  const { data, isLoading, isError, error } = useHolderRealizedPriceChart({
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    limit: limit || undefined,
  });

  const option = useMemo<EChartsOption | null>(() => {
    if (!data) return null;

    const { xAxis, series } = data;

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(20, 20, 30, 0.9)",
        borderColor: "#333",
        textStyle: {
          color: "#fff",
        },
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
          },
        },
        formatter: (params: echarts.TooltipComponentFormatterCallbackParams) => {
          if (!Array.isArray(params) || params.length === 0) return "";

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const p = params as any[];
          const date = p[0].axisValue;
          
          // p[0] is short-term realized price
          // p[1] is long-term realized price
          // p[2] is market price
          const shortRealizedPrice = (p[0]?.data as number) || 0;
          const longRealizedPrice = (p[1]?.data as number) || 0;
          const marketPrice = p[2]?.data as number | undefined;

          // Calculate profit/loss percentages
          const shortProfit = marketPrice 
            ? ((marketPrice - shortRealizedPrice) / shortRealizedPrice * 100).toFixed(1)
            : "0.0";
          const longProfit = marketPrice
            ? ((marketPrice - longRealizedPrice) / longRealizedPrice * 100).toFixed(1)
            : "0.0";

          const shortProfitColor = parseFloat(shortProfit) >= 0 ? "#22c55e" : "#ef4444";
          const longProfitColor = parseFloat(longProfit) >= 0 ? "#22c55e" : "#ef4444";

          return `
            <div style="padding: 8px; min-width: 280px;">
              <div style="font-weight: bold; margin-bottom: 8px; color: #9ca3af;">${date}</div>
              
              <div style="margin-top: 10px; border-top: 1px solid #374151; padding-top: 8px;">
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #3b82f6; margin-right: 6px;"></span>
                  <span style="color: #d1d5db;">Short-term Realized:</span>
                  <span style="margin-left: auto; font-weight: bold; color: #3b82f6; padding-left: 12px;">$${shortRealizedPrice.toLocaleString(
                    "en-US",
                    { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                  )}</span>
                </div>
                <div style="margin-left: 14px; margin-bottom: 8px; color: ${shortProfitColor}; font-weight: bold; font-size: 11px;">
                  ${parseFloat(shortProfit) >= 0 ? "+" : ""}${shortProfit}%
                </div>

                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #8b5cf6; margin-right: 6px;"></span>
                  <span style="color: #d1d5db;">Long-term Realized:</span>
                  <span style="margin-left: auto; font-weight: bold; color: #8b5cf6; padding-left: 12px;">$${longRealizedPrice.toLocaleString(
                    "en-US",
                    { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                  )}</span>
                </div>
                <div style="margin-left: 14px; margin-bottom: 8px; color: ${longProfitColor}; font-weight: bold; font-size: 11px;">
                  ${parseFloat(longProfit) >= 0 ? "+" : ""}${longProfit}%
                </div>

                ${
                  marketPrice !== undefined
                    ? `
                <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #374151;">
                  <div style="display: flex; align-items: center;">
                    <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #fbbf24; margin-right: 6px;"></span>
                    <span style="color: #d1d5db;">Market Price:</span>
                    <span style="margin-left: auto; font-weight: bold; color: #fbbf24; padding-left: 12px;">$${marketPrice.toLocaleString(
                      "en-US",
                      { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                    )}</span>
                  </div>
                </div>
                `
                    : ""
                }
              </div>
            </div>
          `;
        },
      },
      legend: {
        data: [
          "Short Term Holder Realized Price",
          "Long Term Holder Realized Price",
          "BTC Market Price",
        ],
        top: 0,
        right: 0,
        textStyle: {
          color: "#9ca3af",
        },
        icon: "circle",
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "12%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: xAxis,
        axisLine: {
          lineStyle: {
            color: "#374151",
          },
        },
        axisLabel: {
          color: "#9ca3af",
          formatter: (value: string) => {
            const date = new Date(value);
            // For large datasets, show year only
            if (xAxis.length > 365) {
              return date.toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
              });
            }
            return date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          },
        },
      },
      yAxis: [
        {
          type: "value",
          name: "Realized Price (USD)",
          position: "left",
          splitLine: {
            lineStyle: {
              color: "#1f2937",
            },
          },
          axisLabel: {
            color: "#9ca3af",
            formatter: (value: number) => {
              if (value >= 1000) {
                return `$${(value / 1000).toFixed(0)}k`;
              }
              return `$${value}`;
            },
          },
        },
        {
          type: "value",
          name: "Market Price (USD)",
          position: "right",
          splitLine: {
            show: false,
          },
          axisLabel: {
            color: "#9ca3af",
            formatter: (value: number) => {
              if (value >= 1000) {
                return `$${(value / 1000).toFixed(0)}k`;
              }
              return `$${value}`;
            },
          },
        },
      ],
      series: series.map((s, idx) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const baseSeries: any = {
          ...s,
        };

        // idx 0: Short-term realized price (blue)
        // idx 1: Long-term realized price (purple)
        // idx 2: Market price (yellow/gold)
        if (idx === 0) {
          baseSeries.itemStyle = {
            color: "#3b82f6",
          };
          baseSeries.lineStyle = {
            width: 2,
            color: "#3b82f6",
          };
          baseSeries.symbol = "none";
        } else if (idx === 1) {
          baseSeries.itemStyle = {
            color: "#8b5cf6",
          };
          baseSeries.lineStyle = {
            width: 2,
            color: "#8b5cf6",
          };
          baseSeries.symbol = "none";
        } else {
          // Market price
          baseSeries.itemStyle = {
            color: "#fbbf24",
          };
          baseSeries.lineStyle = {
            width: 2,
            color: "#fbbf24",
          };
          baseSeries.symbol = "none";
        }

        return baseSeries;
      }),
      dataZoom: [
        {
          type: "inside",
          start: 0,
          end: 100,
        },
        {
          type: "slider",
          show: true,
          bottom: 30,
          height: 20,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af" },
          brushSelect: false,
        },
      ],
    };
  }, [data]);

  return (
    <EChartsRenderer
      chartOption={option || {}}
      height={height}
      isLoading={isLoading}
      error={isError ? (error as Error).message : null}
      hasData={!!data}
      containerClassName="w-full h-full"
    />
  );
};
